import React, { useEffect, useState } from 'react';
import { useProject, useUpdateProject, useProjectTimezones } from '../../hooks/useProject';
import { useOrganizations } from '../../hooks/useOrganization';
import { FormGroup } from '../../components/FormGroup';
import { AlertBlock } from '../../components/AlertBlock';
import { HibernateModal } from './HibernateModal';
import { ReactivateModal } from './ReactivateModal';
import { DeleteProjectModal } from './DeleteProjectModal';
import { navigateToAngular } from '../../services/navigate';
import { useIsInternalUser } from '../../hooks/useIsInternalUser';
import { SubmitButton } from '../../components/SubmitButton';
import { api } from '../../services/api';
import { showApiErrorToast } from '../../services/errorToast';
import { formatLocaleDate } from '../../utilities/date';
import { capitalize } from '../../utilities/format';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { notifySuccess } from '../../services/notify';

const PAID_NOTE = <span className="text text-muted small">* Enabling this feature will incur additional charges</span>;

function CopyInputGroup({ id, label, value, type = 'text' }: { id: string; label: string; value: string; type?: string }) {
  const copy = () => navigator.clipboard.writeText(value).catch(() => {
    const el = document.createElement('input');
    el.value = value;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  });
  return (
    <FormGroup label={label} htmlFor={id}>
      <div className="input-group">
        <input type={type} id={id} className="form-control" value={value} readOnly />
        <span className="input-group-btn">
          <button className="btn btn-default" type="button" onClick={copy}>
            <i className="fas fa-copy" aria-hidden="true" />
          </button>
        </span>
      </div>
    </FormGroup>
  );
}

export function DetailTab({ projectId }: { projectId: string }) {
  const { data: project, isLoading } = useProject(projectId);
  const { data: timezones = [] } = useProjectTimezones();
  const { data: orgs = [] } = useOrganizations();
  const updateProject = useUpdateProject();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [companyFirmName, setCompanyFirmName] = useState('');
  const [timezoneId, setTimezoneId] = useState('');
  const [redactionBoxWhite, setRedactionBoxWhite] = useState(false);
  const [amazonTranscribeServiceEnabled, setAmazonTranscribeServiceEnabled] = useState(false);
  const [googleTranslateEnabled, setGoogleTranslateEnabled] = useState(false);
  const [enhancedPiiDetection, setEnhancedPiiDetection] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const [showHibernate, setShowHibernate] = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [sftpPassword, setSftpPassword] = useState('••••••••');
  const [showPassword, setShowPassword] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);

  const isIIDUser = useIsInternalUser();

  useEffect(() => {
    if (project && !initialized) {
      setName(project.name);
      setDescription(project.description ?? '');
      setCompanyFirmName(project.companyFirmName ?? '');
      setTimezoneId(project.timezoneId ?? '');
      setRedactionBoxWhite(project.redactionBoxWhite ?? false);
      setAmazonTranscribeServiceEnabled(project.amazonTranscribeServiceEnabled ?? false);
      setGoogleTranslateEnabled(project.googleTranslateEnabled ?? false);
      setEnhancedPiiDetection(project.enhancedPiiDetection ?? false);
      setInitialized(true);
    }
  }, [project, initialized]);

  if (isLoading) return <LoadingPlaceholder />;
  if (!project) return null;

  const org = project.organizationId ? orgs.find(o => o.id === project.organizationId) : undefined;
  // Org projects always carry the org name; fall back to the stored value if the org isn't in the user's list.
  const lockedFirmName = project.organizationId ? (org?.name ?? project.companyFirmName ?? '') : undefined;
  const usageType = project.usageType ? capitalize(project.usageType) : undefined;
  const isActive = project.status === 'active' || project.status === 'trial';
  const saving = updateProject.isPending;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateProject.mutate(
      { id: projectId, name, description, companyFirmName: lockedFirmName ?? companyFirmName, timezoneId,
        usageType: project.usageType,
        redactionBoxWhite, amazonTranscribeServiceEnabled, googleTranslateEnabled, enhancedPiiDetection,
      },
      { onSuccess: () => notifySuccess({ title: 'Project Details Updated', timeout: 5000 }) },
    );
  };

  const handleRevealPassword = async () => {
    if (showPassword) {
      setShowPassword(false);
      setSftpPassword('••••••••');
      return;
    }
    setLoadingPassword(true);
    try {
      const res = await api.get<{ password: string }>(`/project/${projectId}/sftp/password`);
      setSftpPassword(res.password);
      setShowPassword(true);
    } catch (error) {
      showApiErrorToast(error);
    } finally {
      setLoadingPassword(false);
    }
  };

  const copyPassword = () => {
    if (!showPassword || !sftpPassword) return;
    navigator.clipboard.writeText(sftpPassword).catch(() => {});
  };

  const sftp = {
    host: project.sftpServer ?? 'ftp.folio.accessid.legal',
    port: '22',
    username: `project-${projectId}`,
  };

  return (
    <div className="row">
      {/* ── Left column: project form ─────────────────────────────── */}
      <div className="col-md-7">
        <form id="ProjectForm" className="form-horizontal" onSubmit={handleSave} noValidate>

          <h2><i className="fas fa-info" aria-hidden="true" /> Project Details</h2>

          {org && (
            <FormGroup label="Organization" htmlFor="org-name">
              <p className="form-control-static"><i className="fas fa-building" aria-hidden="true" /> {org.name}</p>
            </FormGroup>
          )}
          <FormGroup label="Status" htmlFor="project-status">
            <p className="form-control-static">
              {capitalize(project.status)}
              {project.status === 'hibernate' && project.hibernateDate && (
                <small className="text-muted"> {formatLocaleDate(project.hibernateDate)}</small>
              )}
            </p>
          </FormGroup>
          <FormGroup label="Created" htmlFor="project-created">
            <p className="form-control-static">{formatLocaleDate(project.createdAt)}</p>
          </FormGroup>
          {usageType && (
            <FormGroup label="Usage Type" htmlFor="project-usage">
              <p className="form-control-static">{usageType}</p>
            </FormGroup>
          )}
          {isActive && project.hibernateDate && (
            <FormGroup label="Hibernated" htmlFor="project-hibernated">
              <p className="form-control-static">{formatLocaleDate(project.hibernateDate)}</p>
            </FormGroup>
          )}
          {isActive && project.reactivationDate && (
            <FormGroup label="Re-Activated" htmlFor="project-reactivated">
              <p className="form-control-static">{formatLocaleDate(project.reactivationDate)}</p>
            </FormGroup>
          )}

          <FormGroup label="Name" htmlFor="project-name">
            <input id="project-name" className="form-control" value={name} maxLength={255} required
              onChange={e => setName(e.target.value)} disabled={saving} />
          </FormGroup>
          <FormGroup label="Description" htmlFor="project-description">
            <input id="project-description" className="form-control" value={description} maxLength={255}
              onChange={e => setDescription(e.target.value)} disabled={saving} />
          </FormGroup>
          <FormGroup label="Company/Firm" htmlFor="project-company">
            <input id="project-company" className="form-control" value={lockedFirmName ?? companyFirmName} maxLength={255}
              onChange={e => setCompanyFirmName(e.target.value)} disabled={saving} readOnly={lockedFirmName !== undefined} />
            {lockedFirmName !== undefined && <span className="help-block">Managed in AccessID</span>}
          </FormGroup>
          <FormGroup label="Timezone" htmlFor="project-timezone">
            <select id="project-timezone" className="form-control" value={timezoneId}
              onChange={e => setTimezoneId(e.target.value)} disabled={saving}>
              {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </FormGroup>

          <h2><i className="fas fa-cogs" aria-hidden="true" /> Additional Services</h2>

          <FormGroup label="White Redaction Box" htmlFor="redaction-box">
            <ToggleSwitch checked={redactionBoxWhite} onChange={setRedactionBoxWhite}
              disabled={saving || !isActive} onLabel="ON" offLabel="OFF" />
          </FormGroup>
          <FormGroup label="Transcription" htmlFor="transcription">
            <ToggleSwitch checked={amazonTranscribeServiceEnabled} onChange={setAmazonTranscribeServiceEnabled}
              disabled={saving} onLabel="ON" offLabel="OFF" />
          </FormGroup>
          <FormGroup label="Google Translate" htmlFor="google-translate">
            <ToggleSwitch checked={googleTranslateEnabled} onChange={setGoogleTranslateEnabled}
              disabled={saving} onLabel="ON" offLabel="OFF" />
            {' '}{PAID_NOTE}
          </FormGroup>

          {isIIDUser && (
            <>
              <h2><i className="fas fa-microchip" aria-hidden="true" /> Advanced Analytics & AI</h2>
              <FormGroup label="Enhanced PII Detection" htmlFor="enhanced-pii">
                <ToggleSwitch checked={enhancedPiiDetection} onChange={setEnhancedPiiDetection}
                  disabled={saving} onLabel="ON" offLabel="OFF" />
                {' '}{PAID_NOTE}
              </FormGroup>
            </>
          )}

          <br /><br /><br />

          <div className="form-group">
            <div className="col-sm-9">
              <div className="pull-right">
                <SubmitButton pending={saving}>Save</SubmitButton>
                {' '}
                {isActive && (
                  <button type="button" className="btn btn-default" onClick={() => setShowHibernate(true)}>
                    <i className="fas fa-pause" aria-hidden="true" /> Hibernate
                  </button>
                )}
                {' '}
                {project.status === 'hibernate' && (
                  <button type="button" className="btn btn-default" onClick={() => setShowReactivate(true)}>
                    <i className="fas fa-play" aria-hidden="true" /> Re-Activate
                  </button>
                )}
                {' '}
                {project.status !== 'purged' && (
                  <button type="button" className="btn btn-danger" onClick={() => setShowDelete(true)}>
                    <i className="fas fa-trash" aria-hidden="true" /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>

          {updateProject.isError && <AlertBlock type="danger">Failed to save project details.</AlertBlock>}
        </form>
      </div>

      {showHibernate && (
        <HibernateModal projectId={projectId} projectName={project.name}
          onClose={() => setShowHibernate(false)} />
      )}
      {showReactivate && (
        <ReactivateModal projectId={projectId} projectName={project.name}
          onClose={() => setShowReactivate(false)} />
      )}
      {showDelete && (
        <DeleteProjectModal projectId={projectId} projectName={project.name}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigateToAngular('/project')} />
      )}

      {/* ── Right column: IIO Drive ───────────────────────────────── */}
      <div className="col-md-5">
        <h2><i className="fas fa-cloud" aria-hidden="true" /> IIO Drive Details</h2>
        <p className="text-muted">
          IIO Drive is a secure, encrypted, and private file storage system for your project.
          These files are not indexed, are not searchable, and do not count against your per-gig footprint.
        </p>
        <form className="form-horizontal">
          <CopyInputGroup id="sftp-host" label="Server" value={sftp.host} />
          <CopyInputGroup id="sftp-port" label="Port" value={sftp.port} />
          <CopyInputGroup id="sftp-username" label="Username" value={sftp.username} />

          <FormGroup label="Password" htmlFor="sftp-password">
            <div className="input-group">
              <input
                type={showPassword ? 'text' : 'password'}
                id="sftp-password"
                className="form-control"
                value={sftpPassword}
                readOnly
              />
              <span className="input-group-btn">
                <button className="btn btn-default" type="button" onClick={copyPassword}
                  disabled={!showPassword || !sftpPassword || loadingPassword}
                  title={!showPassword ? 'Reveal first to copy' : 'Copy password'}>
                  <i className="fas fa-copy" aria-hidden="true" />
                </button>
                <button className="btn btn-default" type="button" onClick={handleRevealPassword}
                  disabled={loadingPassword}>
                  {loadingPassword
                    ? <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                    : showPassword
                      ? <i className="fas fa-eye-slash" aria-hidden="true" />
                      : <i className="fas fa-eye" aria-hidden="true" />}
                </button>
              </span>
            </div>
          </FormGroup>
        </form>
      </div>
    </div>
  );
}
